/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable max-lines */

import { getSpecialtyIdentifyTraits } from '@/components/utils/specialty-identify-traits';
import {
  useIsAutoLinkSessionsEnabled,
  useIsFlagProviderSettled,
  useIsProjectsEnabled,
  useIsSessionPageV2Enabled,
} from '@/features/flags';
import {
  accessAllPermissions,
  hasUpdatedAbilityAtom,
  updateAbility,
} from '@/helpers/casl/ability';
import { useAutoCreateEhrProfile } from '@/hooks/integrations/useAutoCreateEhrProfile';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useQueryUserEHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useQueryUserForContext } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useAuth0Session } from '@/hooks/useAuth0Session';
import { useCheckTokenOnTabFocus } from '@/hooks/useCheckTokenExpiry';
import { useDatabaseRegion } from '@/hooks/useDatabaseRegion';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useKinde } from '@/hooks/useKinde';
import { useSetUserRegionFromToken } from '@/hooks/useSetUserRegionFromToken';
import { usePublishSourceControlSyncLifecycle } from '@/hooks/useSyncSourceControlSelections';
import { getPersistedMdAuthToken } from '@/integrations/medical-director/medicalDirectorSidebar';
import { getAppType } from '@/lib/analytics/legacy-sender-context';
import {
  setActiveTeam,
  trackSignInSuccessfulWebAuth,
  trackSignUpSuccessfulWebAuth,
} from '@/lib/analytics/scribe/auth';
import { identify } from '@/lib/analytics/scribe/identify';
import { selectActiveTeamId } from '@/lib/analytics/workspace-analytics-context';
import { clearAuthCache } from '@/lib/auth';
import {
  clearPersistedModels,
  logout,
  setAuthProviderAsPersistentCookie,
  setToken,
} from '@/lib/authActions';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { logoutChannel } from '@/lib/logoutChannel';
import {
  getSessionPageV2EnrollmentSource,
  type SignInMethod,
} from '@/models/analytics/features/user';
import { AuthProvider } from '@/models/authModel';
import { store, type RootState } from '@/store';
import { type IntegrationRequest } from '@/types';
import { isSettledLoggedOut } from '@/utils/auth/sessionSettled';
import { setIntercomProperties } from '@/utils/identify';
import { logger } from '@/utils/logger/logger';
import { clearSignInTracking, getSignInTracking } from '@/utils/signInSessionTracking';
import { setDatadogProperties } from '@/utils/tracing';
import { useHcpVerificationIntercomStatus } from '@heidi/ads/data';
import {
  extractIdpSubFromAccessToken,
  extractRegionFromAccessToken,
} from '@heidi/auth/tokens';
import { integrationOptionsAtom } from '@heidi/integrations/ehr/data/atoms';
import { GET_TEAM_DETAILS } from '@heidi/workspace/contracts';
import { type KindeState, type KindeUser } from '@kinde-oss/kinde-auth-nextjs/types';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isEdge } from 'react-device-detect';
import { useSelector } from 'react-redux';

import type { ChromeRuntime } from '@/components/utils/types';
import { CurrentUserProvider } from '@/context/current-user-context';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { EvidenceBillingSource, EvidenceSubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  useCombinedQueriesTeamGeneralInfo,
  useCurrentWorkspace,
  useQueryEntitlements,
  useQueryJoinedTeams,
  useQueryTeamDetails,
  useWorkspaceId,
} from '@heidi/workspace/data';
import { useTrackDependencyChanges } from './auth.helpers';

// `AuthTokenResolver` + `UserResolver` are the two halves of the auth bootstrap,
// composed around the workspace-hydration gate in `./auth-wrapper.tsx`.
export function AuthTokenResolver({ children }: { children: React.ReactNode }) {
  const { token } = useSelector((rootState: RootState) => rootState.auth);

  // this is always a KindeState when using the next provider and this Auth component
  const kindeAuth = useKinde() as KindeState;
  const kindeToken = kindeAuth.getToken();

  // Auth0 session - will be null if not authenticated via Auth0
  const {
    accessToken: auth0Token,
    isAuthenticated: isAuth0Authenticated,
    isLoading: isAuth0Loading,
  } = useAuth0Session();

  // Determine which auth provider is active and get the active token
  const activeToken = auth0Token || kindeToken;
  const activeAuthProvider = auth0Token ? AuthProvider.AUTH0 : AuthProvider.KINDE;

  const kindeUser = kindeAuth.user as KindeUser<Record<string, string>> | null;

  // Subscribe in an effect so the listener is removed on unmount. The channel
  // is a module singleton: a handler assigned during render outlives this
  // component, so a tab that client-navigates from `/` to a minimal-providers
  // page (e.g. `/integration/widget/auth`) would keep reacting to other tabs'
  // logouts there, and strand the widget auth flow on `/` with no return path.
  useEffect(() => {
    logoutChannel.onmessage = (message) => {
      if (message.logout === true) {
        if (!isLogoutInFlight()) {
          // Clear the auth token cache synchronously on receiving a
          // cross-tab logout signal, before dispatching the local logout
          // effect. Without this, the cache could hand out the old token
          // to in-flight reads on this tab between the cross-tab signal
          // arriving and the local logout effect completing. The logout
          // effect (`authModel.logout`) also calls `clearAuthCache`
          // internally — this sync call is belt-and-braces on top so the
          // ordering the comment describes is what actually happens.
          clearAuthCache({ reason: 'logout' });
          void logout({ audioWipeId: message.audioWipeId });
        }
      }
    };

    return () => {
      logoutChannel.onmessage = null;
    };
  }, []);

  useCheckTokenOnTabFocus();

  // Track sign in / sign up event only once per session when the user is
  // logged in. `screenHint=signin` → fires `UserSignedIn`,
  // `screenHint=signup` → fires `UserSignedUp` immediately (post-auth), so
  // the v2 sign-up event reflects auth success rather than waiting for the
  // user to complete personalization.
  const hasFiredSignIn = useRef(false);

  useEffect(() => {
    const { method, screenHint } = getSignInTracking();
    if (!activeToken || hasFiredSignIn.current) return;

    clearSignInTracking();
    if (method === null) return;
    hasFiredSignIn.current = true;

    const signInMethod = (method || undefined) as SignInMethod | undefined;

    if (screenHint === 'signin') {
      trackSignInSuccessfulWebAuth({
        auth_provider: activeAuthProvider,
        sign_in_method: signInMethod as SignInMethod,
      });
    } else if (screenHint === 'signup') {
      trackSignUpSuccessfulWebAuth({
        auth_provider: activeAuthProvider,
        sign_in_method: signInMethod as SignInMethod,
      });
    }
  }, [activeToken, activeAuthProvider]);

  // Set token from whichever auth provider is active (Auth0 or Kinde)
  useEffect(() => {
    if (activeToken) {
      setToken(activeToken);
      setAuthProviderAsPersistentCookie(activeAuthProvider);

      // send token to extension
      const extensionId = isEdge
        ? process.env.NEXT_PUBLIC_EDGE_EXTENSION_ID
        : process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID;
      const heartbeat_message = {
        type: 'HEIDI_EXTENSION_HEARTBEAT',
      };
      const token_message = {
        type: 'HEIDI_EXTENSION_SET_TOKEN',
        token: activeToken,
      };

      const chromeRuntime = (globalThis as any).chrome?.runtime as
        | ChromeRuntime
        | undefined;

      if (!extensionId || typeof chromeRuntime?.sendMessage !== 'function') return;
      try {
        void chromeRuntime.sendMessage(
          extensionId,
          heartbeat_message,
          (response: { success: boolean } | null) => {
            if (!response?.success) return;
            void chromeRuntime.sendMessage?.(extensionId, token_message);
          }
        );
      } catch (e) {
        console.log(e);
      }
    }
  }, [activeToken, activeAuthProvider]);

  // Extract region from auth token and set it in the store (Auth0 or Kinde)
  const auth0Region = auth0Token ? extractRegionFromAccessToken(auth0Token) : undefined;
  useSetUserRegionFromToken(auth0Region ?? kindeUser?.properties?.region);

  const router = useRouter();
  useEffect(() => {
    // leftover token but already logged out. state wasn't cleared happens when logging out using /api/auth/logout but not button/function
    // Check both Kinde and Auth0 - if neither is authenticated and we have a token, clear it
    const isKindeLoggedOut = isSettledLoggedOut(
      kindeAuth.isAuthenticated,
      kindeAuth.isLoading
    );
    const isAuth0LoggedOut = isSettledLoggedOut(isAuth0Authenticated, isAuth0Loading);
    // MD-only carve-out: inside the Medical Director Sidebar widget, WebView2
    // can wipe Kinde/Auth0 cookies between sessions, which makes both SDKs
    // report "not authenticated" even though the user never logged out. If we
    // have a token persisted in Sidebar.Settings we're deliberately riding
    // through that wipe — don't clear Redux here. The restore effect in
    // `medicalDirector.tsx` rehydrates from Sidebar.Settings. Stale tokens
    // still get reconciled via `useCheckTokenOnTabFocus`, the 401 path, and
    // explicit logout — this only skips the Kinde-says-not-authed shortcut.
    const mdToken = getPersistedMdAuthToken();
    const hasMdPersistedToken =
      mdToken !== null && !!mdToken.expiresAt && mdToken.expiresAt > Date.now();
    if (
      isKindeLoggedOut &&
      isAuth0LoggedOut &&
      token &&
      !hasMdPersistedToken &&
      router.isReady &&
      !router.asPath.includes('/external/')
    ) {
      logger.info('[Auth] Clearing persisted models', {
        isKindeAuthenticated: kindeAuth.isAuthenticated,
        isKindeLoading: kindeAuth.isLoading,
        isAuth0Authenticated,
        isAuth0Loading,
        hasToken: !!token,
      });
      void clearPersistedModels();
    }
  }, [
    kindeAuth,
    isAuth0Authenticated,
    isAuth0Loading,
    token,
    router.isReady,
    router.asPath,
  ]);

  return <>{children}</>;
}

export function UserResolver({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  // this is always a KindeState when using the next provider and this Auth component
  const kindeAuth = useKinde() as KindeState;
  const kindeToken = kindeAuth.getToken();

  // Auth0 session - will be null if not authenticated via Auth0
  const {
    accessToken: auth0Token,
    user: auth0User,
    isLoading: isUserAuth0Loading,
    // userRefId: auth0UserRefId, todo use this once we switch to user_ref_id for all platforms
    kindeUserId: auth0KindeUserId,
  } = useAuth0Session();

  // Determine which auth provider is active and get the active token
  const activeToken = auth0Token || kindeToken;

  const { data: joinedTeamsData, isFetched: isJoinedTeamsFetched } =
    useQueryJoinedTeams();
  //TODO: Fix tracking once Accuro is live
  const { data: userEhr } = useQueryUserEHR();
  const {
    integrationEhrProvider,
    integrationConnectionType,
    integrationOnboardingProvider,
    integrationOnboardingCreatedAt,
    integrationOnboardingUpdatedAt,
  } = useIntegrationEhrInfo();

  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  // Active team = the joined team in the current workspace (one team per
  // workspace), falling back to the first joined team when there's no match.
  const activeTeamId = selectActiveTeamId(joinedTeamsData?.joined_teams, workspaceId);
  const { data: teamDetails } = useQueryTeamDetails(activeTeamId ?? '');
  const userQuery = useQueryUserForContext();
  const { data: user } = userQuery;
  const databaseRegion = useDatabaseRegion();
  const hcpVerificationStatus = useHcpVerificationIntercomStatus();
  const { data: entitlements } = useQueryEntitlements();
  const enabledEntitlementFeatures = useMemo(
    () =>
      entitlements
        ? Array.from(entitlements.values())
            .filter((entitlement) => entitlement.enabled)
            .map((entitlement) => entitlement.name)
            .toSorted()
        : undefined,
    [entitlements]
  );
  // Auto-link RUM stamp inputs, read here so the identify callback below can
  // fold them into `setDatadogProperties` (see that call). `autoLinkFlagOn` is
  // withheld until the provider settles so a pre-settle default `false` never
  // stamps for a user whose flag is on.
  const isAutoLinkSessionsEnabled = useIsAutoLinkSessionsEnabled();
  const isFlagProviderSettled = useIsFlagProviderSettled();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const workspace = useCurrentWorkspace();
  const { evidenceBilling, isInFreeTrial, hasTrialExpired } = useEvidenceBillingInfo();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);
  const { refetch: refetchDocumentTemplates } = useQueryDocumentTemplates();

  const integrationOptions = useAtomValue(integrationOptionsAtom);

  const { userRoleList, isLoadingTeamDetails } = useCombinedQueriesTeamGeneralInfo();

  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const isProjectsEnabled = useIsProjectsEnabled();

  useAutoCreateEhrProfile();

  useEffect(() => {
    if (activeToken) {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: activeTeamId }],
      });
      void refetchDocumentTemplates();
    }
  }, [activeTeamId, activeToken, queryClient, refetchDocumentTemplates]);

  const setHasUpdatedAbility = useSetAtom(hasUpdatedAbilityAtom);

  const kindeUser = kindeAuth.user as KindeUser<Record<string, string>> | null;

  // Determine active user - Auth0 user takes priority if authenticated
  const activeUser = useMemo(() => {
    if (auth0User) {
      const resolvedId = auth0KindeUserId ?? user?.kinde_user_id;
      if (!resolvedId) return null;
      return {
        id: resolvedId,
        email: auth0User.email ?? user?.email ?? '',
        given_name: user?.first_name ?? '',
        family_name: user?.last_name ?? '',
      };
    }
    if (kindeUser) {
      return {
        id: kindeUser.id ?? '',
        email: kindeUser.email ?? '',
        given_name: kindeUser.given_name ?? '',
        family_name: kindeUser.family_name ?? '',
      };
    }
    return null;
  }, [
    auth0User,
    kindeUser,
    auth0KindeUserId,
    user?.first_name,
    user?.last_name,
    user?.email,
    user?.kinde_user_id,
  ]);

  usePublishSourceControlSyncLifecycle({
    isAuthenticated: Boolean(activeToken),
    isAuthenticationLoading: Boolean(kindeAuth.isLoading || isUserAuth0Loading),
    hasFreshUserData: userQuery.isFetchedAfterMount,
    isUserQueryError: userQuery.isError,
  });

  useTrackDependencyChanges('identify', {
    'billingInfo?.tier': billingInfo?.tier,
    integrationOptions: integrationOptions,
    activeToken: activeToken,
    activeUser: activeUser,
    teamDetails: teamDetails,
    'user?.user_ref_id': user?.user_ref_id,
    'user?.integration_requests': user?.integration_requests,
    'user?.country': user?.country,
    'user?.session_settings?.opted_in_experimental':
      user?.session_settings?.opted_in_experimental,
    'user?.tier': user?.tier,
    'user?.user_mfa_info?.is_mfa_enabled': user?.user_mfa_info?.is_mfa_enabled,
    'user?.user_mfa_info?.set_by': user?.user_mfa_info?.set_by,
    'user?.ehr_provider': user?.ehr_provider,
    integrationEhrProvider: integrationEhrProvider,
    integrationConnectionType: integrationConnectionType,
    'billingInfo?.third_party_license?.latest_tier':
      billingInfo?.third_party_license?.latest_tier,
    'user?.specialty_id': user?.specialty_id,
    'user?.other_specialty': user?.other_specialty,
    'user?.evidence_access': user?.evidence_access,
    'user?.is_evidence_activated': user?.is_evidence_activated,
    'user?.language_time_settings?.display_language':
      user?.language_time_settings?.display_language,
    userRoleList: userRoleList,
    specialties: specialties,
    databaseRegion: databaseRegion,
    component: 'Auth',
  });

  const sessionPageV2EnrollmentSource = getSessionPageV2EnrollmentSource(user);

  // Debounced analytics identify function
  const analyticsIdentify = useCallback(() => {
    // if logged in
    // prevent if router is at index page
    if (router.pathname === '/') {
      return;
    }

    if (activeUser && activeToken && store.select.auth.getToken(store.getState())) {
      identify({
        authID: activeUser.id || '',
        userTraits: {
          email: activeUser.email || '',
          user_ref_id: user?.user_ref_id,
          App: getAppType(),
          country: user?.country || '',
          given_name: activeUser.given_name || '',
          family_name: activeUser.family_name || '',
          phone_number: user?.phone_number || '',
          user_team_id: teamDetails?.team.id,
          user_team_name: teamDetails?.team.name,
          user_team_role: teamDetails?.team.team_members
            ? teamDetails.team.team_members.find(
                (member) => member.kinde_user_id === activeUser.id
              )?.user_role
            : undefined,
          user_team_plan: teamDetails?.team.team_members
            ? teamDetails.team.team_members.find(
                (member) => member.kinde_user_id === activeUser.id
              )?.tier
            : undefined,
          user_joined_team_date: teamDetails?.team.team_members
            ? teamDetails.team.team_members.find(
                (member) => member.kinde_user_id === activeUser.id
              )?.created_at
            : undefined,
          user_created_date: user?.created_at,
          user_tier: user?.tier,
          region: user?.country ? countriesToRegion[user?.country] : undefined,
          user_role_list: userRoleList,
          team_tier: teamDetails?.team.tier,
          effective_tier:
            billingInfo?.third_party_license?.latest_tier || billingInfo?.tier,
          onboarding_selected_ehr_provider: user?.ehr_provider ?? null,
          ehr_provider: integrationEhrProvider ?? null,
          has_integration: !!integrationConnectionType,
          // Flag-targeting cohort properties: all three describe the same
          // IntegrationOnboardingSession document, so a release condition can
          // AND the provider with both timestamps without straddling sessions.
          // Deliberately not reusing `ehr_provider` above — on desktop
          // `tauriAuth.tsx` also identifies and sets that key from the legacy
          // EHR profile, so whichever identify fires last wins and it is not a
          // dependable signal for the onboarding-session cohort.
          integration_onboarding_provider: integrationOnboardingProvider ?? null,
          integration_onboarding_created_at: integrationOnboardingCreatedAt ?? null,
          integration_onboarding_updated_at: integrationOnboardingUpdatedAt ?? null,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          source: integrationOptions?.ehrName || 'Heidi',
          signup_attributed_product: user?.signup_source,
          integration_requests: user?.integration_requests as IntegrationRequest[],
          mfa_enabled: user?.user_mfa_info?.is_mfa_enabled,
          mfa_trigger_source: user?.user_mfa_info?.set_by,
          user_team_mfa_enabled: !!teamDetails?.team.mfa_info?.method,
          opted_in_experimental: user?.session_settings?.opted_in_experimental,
          session_page_v2_enrollment_source: sessionPageV2EnrollmentSource,
          go_live_time_stamp: teamDetails?.team.go_live_time_stamp,
          onboarding_free_text: teamDetails?.team.onboarding_free_text,
          ...getSpecialtyIdentifyTraits(user?.specialty_id, specialties),
          other_specialty: user?.other_specialty,
          display_language: user?.language_time_settings?.display_language,
          seen_modals: user?.seen_modals,
          // Kept for Amplitude / Segment / Datadog RUM, which carry no
          // per-identify cost. It is stripped from the PostHog payload only —
          // see the destination fan-out in `analyticsScribeModel.identify`.
          pathname: router.pathname,
          from: 'auth.tsx',
          evidence_billing_info: {
            tier: evidenceBilling?.tier ?? EvidenceSubscriptionTier.FREE,
            source: evidenceBilling?.source ?? EvidenceBillingSource.user,
            is_in_free_trial: isInFreeTrial ?? false,
            has_trial_expired: hasTrialExpired ?? false,
            is_stripe_managed: evidenceBilling?.is_stripe_managed ?? true,
          },
          evidence_access: user?.evidence_access ?? undefined,
          is_evidence_activated: user?.is_evidence_activated ?? undefined,
          is_managed: !!user?.is_managed,
          // Always sync entitlement features (instead of only when there are enabled features)
          features: enabledEntitlementFeatures,
        },
      });

      setDatadogProperties(activeUser.id || '', {
        userTier: user?.tier,
        teamTier: teamDetails?.team.tier,
        teamId: teamDetails?.team.id,
        teamName: teamDetails?.team.name ?? null,
        workspaceId: workspace?.workspace_id ?? null,
        ehrProvider: integrationEhrProvider,
        hasIntegration: !!integrationConnectionType,
        source: integrationOptions?.ehrName || 'Heidi',
        userRefId: user?.user_ref_id,
        idpSub: activeToken ? extractIdpSubFromAccessToken(activeToken) : undefined,
        // Re-applied on every identify because `setUser` above drops all
        // `@usr.*`. `autoLinkIntent` is the stored server value — identify does
        // NOT depend on `intends_to_auto_link_sessions`, so it never fires
        // during the toggle's optimistic-cache window and reads server truth.
        autoLinkFlagOn: isFlagProviderSettled ? isAutoLinkSessionsEnabled : null,
        autoLinkIntent: user?.session_settings?.intends_to_auto_link_sessions ?? null,
      });
    }
    // ignore missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingInfo?.tier,
    evidenceBilling,
    isInFreeTrial,
    hasTrialExpired,
    integrationConnectionType,
    integrationEhrProvider,
    // Needed even though the provider rarely changes on its own: a re-onboard
    // of the SAME provider bumps only `updated_at`, which would otherwise
    // leave a stale timestamp on the PostHog person (the exhaustive-deps rule
    // is suppressed on this array, so it cannot catch the omission).
    integrationOnboardingProvider,
    integrationOnboardingCreatedAt,
    integrationOnboardingUpdatedAt,
    integrationOptions,
    activeToken,
    activeUser,
    teamDetails,
    workspace,
    workspaceId,
    user?.user_ref_id,
    user?.integration_requests,
    user?.country,
    user?.session_settings?.opted_in_experimental,
    sessionPageV2EnrollmentSource,
    user?.tier,
    user?.user_mfa_info?.is_mfa_enabled,
    user?.user_mfa_info?.set_by,
    user?.ehr_provider,
    billingInfo?.third_party_license?.latest_tier,
    user?.specialty_id,
    user?.other_specialty,
    user?.evidence_access,
    user?.is_evidence_activated,
    enabledEntitlementFeatures,
    user?.language_time_settings?.display_language,
    userRoleList,
    specialties,
    router.pathname,
    // Re-run identify (and thus re-stamp the auto-link @usr.* keys) when the
    // flag settles or its eval flips. `intends_to_auto_link_sessions` is
    // deliberately NOT a dep — that is what keeps identify from firing during
    // the toggle's optimistic-cache window, so the stamp reads server truth.
    isAutoLinkSessionsEnabled,
    isFlagProviderSettled,
  ]);

  // Trigger debounced analytics identify when dependencies change
  useEffect(() => {
    if (activeUser) {
      analyticsIdentify();
    }
  }, [analyticsIdentify, activeUser]);

  // The identify above persists these on the PostHog person, but that write can
  // lag flag evaluation. `setPersonPropertiesForFlags` sends the values *with*
  // the evaluation request (and reloads flags), so a release condition keyed on
  // them resolves correctly on the first reload after the onboarding-sessions
  // query lands rather than the next one. Mirrors the `region` pattern in
  // `useQueryKindeProperties`.
  //
  // Only published once a provider is resolved: an absent property simply does
  // not match a condition, so pushing nulls for the (majority) non-integrated
  // users would cost a flag reload and buy nothing.
  useEffect(() => {
    if (!integrationOnboardingProvider) return;

    posthog.setPersonPropertiesForFlags({
      integration_onboarding_provider: integrationOnboardingProvider,
      integration_onboarding_created_at: integrationOnboardingCreatedAt ?? null,
      integration_onboarding_updated_at: integrationOnboardingUpdatedAt ?? null,
    });
  }, [
    integrationOnboardingProvider,
    integrationOnboardingCreatedAt,
    integrationOnboardingUpdatedAt,
  ]);

  useEffect(() => {
    if (teamDetails?.team) {
      setActiveTeam(teamDetails.team);
    }
  }, [teamDetails?.team]);

  useEffect(() => {
    if (user?.intercom_user_id) {
      void setIntercomProperties({
        user,
        teamDetails,
        userEhr,
        integrationOptions,
        billingInfo,
        workspace,
        locale: user?.language_time_settings?.display_language,
        isSessionPageV2Enabled,
        isProjectsEnabled,
        databaseRegion,
        hcpVerificationStatus,
      });
    }
  }, [
    user,
    teamDetails?.team.id,
    teamDetails,
    userEhr,
    integrationOptions,
    billingInfo,
    workspace,
    user?.language_time_settings?.display_language,
    isSessionPageV2Enabled,
    isProjectsEnabled,
    databaseRegion,
    hcpVerificationStatus,
  ]);

  // update ability
  useEffect(() => {
    // wait until both queries are fetched
    if (!isJoinedTeamsFetched) {
      return;
    }

    if (isLoadingTeamDetails) {
      return;
    }

    // if user role list, set corresponding permissions
    if (userRoleList) {
      const uniquePermissions = Array.from(
        new Map(
          userRoleList
            .flatMap((role) => role.permissions)
            .map((permission) => [permission.code, permission])
        ).values()
      );

      updateAbility({ permissions: uniquePermissions });
      setHasUpdatedAbility(true);
    } else {
      accessAllPermissions();
    }
  }, [userRoleList, isJoinedTeamsFetched, isLoadingTeamDetails, setHasUpdatedAbility]);

  return <CurrentUserProvider userQuery={userQuery}>{children}</CurrentUserProvider>;
}
